import { Link } from "react-router-dom";
import LottieAnimation from "./LottieAnimation";
import CitationTooltip from "./CitationTooltip";

// Citation data constants for consistent tooltip information
const CITATIONS = {
  stanford2025: {
    source: "Stanford Institute for Human-Centered AI (HAI)",
    title: "2025 AI Index Report",
    year: "April 2025",
    description:
      "AI inference costs dropped 280-fold from $20 per million tokens (Nov 2022) to $0.07 (Oct 2024)",
  },
  mckinsey2025: {
    source: "McKinsey & Company",
    title: "The State of AI: How Organizations Are Rewiring to Capture Value",
    year: "March 2025",
    description:
      "Comprehensive survey showing 78% AI adoption rate and implementation success factors",
  },
  deloitte2025: {
    source: "Deloitte",
    title: "State of Generative AI in the Enterprise Q4 2024",
    year: "2025",
    description:
      "20% of organizations report ROI exceeding 30% for most advanced AI initiatives",
  },
  bcg2024: {
    source: "Boston Consulting Group",
    title: "AI Implementation Success Factors",
    year: "2024",
    description:
      "Analysis showing first value typically achieved within 6-12 months of AI implementation",
  },
  jama2024: {
    source: "Journal of the American Medical Association (JAMA)",
    title: "AI in Clinical Documentation",
    year: "2024",
    description:
      "Study documenting 1.5-2 hours daily time savings for physicians using AI documentation tools",
  },
  wef2025: {
    source: "World Economic Forum",
    title: "AI in Manufacturing Report",
    year: "2025",
    description:
      "Research showing up to 25% reduction in quality defects through AI-powered inspection systems",
  },
} as const;

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gray-50 py-20 px-6" aria-labelledby="hero-heading">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-15 items-center">
            <div>
              <h1
                id="hero-heading"
                className="text-[64px] leading-[1.1] font-normal mb-6 text-text-dark tracking-[-2px]"
              >
                Beyond Automation
                <br />- Into Intelligence
              </h1>
              <p className="text-xl text-text-gray mb-8">
                The Next Decade Will Compress a Century of Progress
                <br />
                While the world debates AI, Bangladesh leaps forward.
              </p>
            </div>
            <div className="flex justify-center items-center max-w-[400px] mx-auto">
              <LottieAnimation
                src="/animations/hero-animation-refined.json"
                trigger="all"
                loop={true}
                autoplay={true}
                speed={1}
                className="w-80 h-80"
                fallback={
                  <div
                    className="grid grid-cols-2 gap-8 justify-center items-center max-w-[320px] mx-auto"
                    role="img"
                    aria-label="Abstract geometric shapes representing AI and technology"
                  >
                    {/* Fallback: Original Animated Shapes */}
                    <svg
                      className="w-36 h-36 justify-self-center cursor-pointer transition-transform duration-300 ease-out hover:scale-110"
                      aria-label="Red circle shape"
                      role="img"
                    >
                      <circle cx="72" cy="72" r="60" fill="#FF3621" />
                    </svg>
                    <svg
                      className="w-36 h-36 justify-self-center cursor-pointer transition-transform duration-300 ease-out hover:scale-110"
                      aria-label="Red triangle shape"
                      role="img"
                    >
                      <polygon points="72,12 132,120 12,120" fill="#FF3621" />
                    </svg>
                    <svg
                      className="w-36 h-36 justify-self-center cursor-pointer transition-transform duration-300 ease-out hover:scale-110"
                      aria-label="Red square shape"
                      role="img"
                    >
                      <rect
                        x="17"
                        y="17"
                        width="110"
                        height="110"
                        fill="#FF3621"
                      />
                    </svg>
                    <svg
                      className="w-36 h-36 justify-self-center cursor-pointer transition-transform duration-300 ease-out hover:scale-110"
                      aria-label="Red hexagon shape"
                      role="img"
                    >
                      <polygon
                        points="72,19 118,44 118,100 72,125 26,100 26,44"
                        fill="#FF3621"
                      />
                    </svg>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Partnership Promise Section */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <div className="text-[#FF3621] text-xl font-bold tracking-wide uppercase mb-4">
              THE PERFECT MOMENT
            </div>
            <h2 className="text-5xl font-normal mb-4 text-text-dark tracking-[-1px]">
              Why Now? Why Tecbyte?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Promise Item 1 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    AI technology is finally ready for business
                  </p>
                </div>
              </div>
            </div>

            {/* Promise Item 2 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    <CitationTooltip citation={CITATIONS.stanford2025}>
                      AI inference costs have dropped 280-fold in 2 years
                    </CitationTooltip>
                  </p>
                </div>
              </div>
            </div>

            {/* Promise Item 3 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    Bangla language AI is now possible
                  </p>
                </div>
              </div>
            </div>

            {/* Promise Item 4 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    Early movers get dedicated support
                  </p>
                </div>
              </div>
            </div>

            {/* Promise Item 5 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    Tomorrow's advantages are built today
                  </p>
                </div>
              </div>
            </div>

            {/* Promise Item 6 */}
            <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <div>
                  <p className="text-text-dark leading-relaxed">
                    The right partner makes all the difference
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Center Statement and CTA */}
          <div className="text-center">
            <p className="text-xl text-text-dark mb-8 font-medium">
              The perfect moment is now.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-accent-red text-white px-8 py-4 rounded-lg font-medium hover:bg-accent-red-hover transition-colors text-lg"
            >
              Seize It Together
            </Link>
          </div>
        </div>
      </section>

      {/* AI Competitive Advantage Section */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-normal mb-4 text-text-dark tracking-[-1px] leading-tight">
              AI Isn't Magic—It's Your Next Competitive Advantage
            </h2>
            <p className="text-xl md:text-2xl text-slate-500 mb-8 leading-relaxed">
              Powered by the industry's most advanced models:
              <br />
              <CitationTooltip
                citation={{
                  source: "Anthropic",
                  title: "Introducing Claude 4",
                  year: "May 2025",
                  description: "72.5% accuracy on SWE-bench coding benchmark",
                }}
              >
                Claude 4.0 (72.5% coding accuracy)
              </CitationTooltip>{" "}
              •{" "}
              <CitationTooltip
                citation={{
                  source: "OpenAI",
                  title: "OpenAI o3 Technical Report",
                  year: "December 2024",
                  description: "96.7% accuracy on AIME mathematics reasoning",
                }}
              >
                GPT o3 (96.7% math reasoning)
              </CitationTooltip>{" "}
              •{" "}
              <CitationTooltip
                citation={{
                  source: "Google DeepMind",
                  title: "Gemini 2.5: Our Newest Model with Thinking",
                  year: "March 2025",
                  description:
                    "2M token context window for long-form reasoning",
                }}
              >
                Gemini 2.5 Pro (2M context)
              </CitationTooltip>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Today's AI */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-accent-red rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 1H7V3H5C3.9 3 3 3.9 3 5V7H1V9H3V11H1V13H3V15H1V17H3V19C3 20.1 3.9 21 5 21H7V23H9V21H11V23H13V21H15V23H17V21H19C20.1 21 21 20.1 21 19V17H23V15H21V13H23V11H21V9H23V7H21V5C21 3.9 20.1 3 19 3H17V1H15V3H13V1H11V3H9V1ZM19 19H5V5H19V19ZM7 7V17H17V7H7ZM9 9H15V15H9V9Z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-text-dark">
                Today's AI
              </h3>
              <p className="text-text-gray leading-relaxed text-lg mb-5">
                A country of geniuses in a datacenter working 24/7 for you.
                Processing millions of decisions, learning from every
                interaction, never sleeping.
              </p>
              <div className="pt-5 border-t border-gray-100">
                <p className="text-sm text-slate-500 italic flex items-center justify-center gap-2">
                  <span>💰</span>
                  <CitationTooltip citation={CITATIONS.deloitte2025}>
                    Mature implementations achieve 3-4x ROI
                  </CitationTooltip>
                  <br />—
                  <CitationTooltip citation={CITATIONS.stanford2025}>
                    costs dropped 280-fold in 2 years
                  </CitationTooltip>
                </p>
              </div>
            </div>

            {/* Tomorrow's Reality */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-accent-red rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-text-dark">
                Tomorrow's Reality
              </h3>
              <p className="text-text-gray leading-relaxed text-lg mb-5">
                Every employee amplified, every decision enhanced. Your entire
                organization operating at superhuman efficiency.
              </p>
              <div className="pt-5 border-t border-gray-100">
                <p className="text-sm text-slate-500 italic flex items-center justify-center gap-2">
                  <span>🚀</span>
                  <CitationTooltip citation={CITATIONS.mckinsey2025}>
                    78% of organizations now using AI
                  </CitationTooltip>
                  <br />
                  —up from 55% in 2023
                </p>
              </div>
            </div>

            {/* Your Timeline */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-accent-red rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-text-dark">
                Your Timeline
              </h3>
              <p className="text-text-gray leading-relaxed text-lg mb-5">
                Not decades away. Months, with the right partner. The technology
                exists today—you just need the bridge to reach it.
              </p>
              <div className="pt-5 border-t border-gray-100">
                <p className="text-sm text-slate-500 italic flex items-center justify-center gap-2">
                  <span>⏱️</span>
                  <CitationTooltip citation={CITATIONS.bcg2024}>
                    First value typically within 6-12 months
                  </CitationTooltip>
                  <br />
                  <CitationTooltip citation={CITATIONS.mckinsey2025}>
                    74% meet or exceed expectations
                  </CitationTooltip>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section (New) */}
      <section id="services" className="py-24 px-6">
        <div className="max-w-[1200px] mx-auto">
          {/* Header with Animation - Hero Style Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-16 items-center mb-20">
            <div>
              <div className="text-[#FF3621] text-xl font-bold tracking-wide uppercase mb-4">
                SERVICES
              </div>
              <h2 className="text-5xl font-normal mb-4 text-text-dark tracking-[-1px]">
                Transform decades into months. Start today.
              </h2>
              <p className="text-lg text-text-gray">
                From proof-of-concept to production-ready AI that transforms how
                Bangladesh works
              </p>
            </div>
            <div className="flex justify-center items-center max-w-[400px] mx-auto">
              <LottieAnimation
                src="/animations/services-animation-themed.json"
                trigger="scroll"
                loop={true}
                autoplay={false}
                speed={1}
                className="w-80 h-80"
                fallback={
                  <div className="w-80 h-80 bg-bg-light rounded-full flex items-center justify-center">
                    <div className="text-text-gray text-lg">
                      Services Animation
                    </div>
                  </div>
                }
              />
            </div>
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Ignite Your AI Revolution */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-red to-red-600 rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold leading-tight mb-4 text-text-dark">
                Ignite Your AI Revolution
              </h3>
              <div className="text-center mb-4">
                <div className="text-[#FF3621] text-lg">
                  From skeptic to strategist in weeks, not years
                </div>
              </div>
              <p className="text-text-gray text-[15px] mb-4 text-center">
                Transform your organization's relationship with AI from
                uncertainty to unstoppable competitive advantage
              </p>
              <ul className="text-left text-text-gray text-[15px] space-y-2">
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Discover hidden millions in AI opportunities
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Transform your leadership into AI champions
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Build unstoppable implementation roadmaps
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Create your competitive moat
                </li>
              </ul>
              <div className="border-t border-gray-200 mt-6 pt-4">
                <div className="text-sm text-gray-500 italic">
                  Explore the Possibilities
                </div>
              </div>
            </div>

            {/* Deploy Intelligence That Delivers */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-red to-red-600 rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z" />
                  <path
                    d="M10 14l-2-2 1.41-1.41L10 11.17l4.59-4.58L16 8l-6 6z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold leading-tight mb-4 text-text-dark">
                Deploy Intelligence That Delivers
              </h3>
              <div className="text-center mb-4">
                <div className="text-[#FF3621] text-lg">
                  Where cutting-edge AI meets real-world results
                </div>
              </div>
              <p className="text-text-gray text-[15px] mb-4 text-center">
                Custom AI solutions that integrate seamlessly with your existing
                systems and deliver measurable business impact
              </p>
              <ul className="text-left text-text-gray text-[15px] space-y-2">
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Custom AI that speaks your business language
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Seamless fusion with legacy systems
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Enterprise-grade LLM, vision, and prediction engines
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Government solutions that actually work
                </li>
              </ul>
              <div className="border-t border-gray-200 mt-6 pt-4">
                <div className="text-sm text-gray-500 italic">
                  Built for Your Success
                </div>
              </div>
            </div>

            {/* Dominate Through AI Excellence */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-red to-red-600 rounded-full mx-auto mb-6 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-8 h-8"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold leading-tight mb-4 text-text-dark">
                Dominate Through AI Excellence
              </h3>
              <div className="text-center mb-4">
                <div className="text-[#FF3621] text-lg">
                  Never fall behind again
                </div>
              </div>
              <p className="text-text-gray text-[15px] mb-4 text-center">
                Build sustainable competitive advantages with AI systems that
                continuously improve and adapt to your evolving needs
              </p>
              <ul className="text-left text-text-gray text-[15px] space-y-2">
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  AI that improves while you sleep
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Bulletproof governance for regulated industries
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Performance gains that compound monthly
                </li>
                <li className="relative pl-5">
                  <span className="absolute left-0 text-[#FF3621]">•</span>
                  Your always-on AI advantage
                </li>
              </ul>
              <div className="border-t border-gray-200 mt-6 pt-4">
                <div className="text-sm text-gray-500 italic">
                  Your Continuous Edge
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* From Vision to Reality in Three Steps */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-normal mb-8 text-text-dark tracking-[-1px] leading-tight">
              From Vision to Reality in Three Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1: Ignite */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-16 h-16 bg-accent-red rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                1
              </div>
              <h3 className="text-2xl font-bold mb-4 text-text-dark">Ignite</h3>
              <p className="text-lg text-[#FF3621] mb-4">
                Two weeks to see your AI future
              </p>
              <p className="text-text-gray leading-relaxed text-lg">
                Find your hidden millions. Build your transformation roadmap.
                Get leadership aligned and excited.
              </p>
            </div>

            {/* Step 2: Deploy */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-16 h-16 bg-accent-red rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                2
              </div>
              <h3 className="text-2xl font-bold mb-4 text-text-dark">Deploy</h3>
              <p className="text-lg text-[#FF3621] mb-4">
                Months, not years to first value
              </p>
              <p className="text-text-gray leading-relaxed text-lg">
                Start with quick wins that fund the journey. Scale what works,
                pivot what doesn't. Build capability while delivering results.
              </p>
            </div>

            {/* Step 3: Dominate */}
            <div className="group bg-white border border-border-light rounded-xl p-12 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-16 h-16 bg-accent-red rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                3
              </div>
              <h3 className="text-2xl font-bold mb-4 text-text-dark">
                Dominate
              </h3>
              <p className="text-lg text-[#FF3621] mb-4">
                Continuous advantage, compounding returns
              </p>
              <p className="text-text-gray leading-relaxed text-lg">
                AI that improves while you sleep. Governance that protects while
                enabling. A moat that widens every day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions by Industry */}
      <section id="solutions-by-industry" className="bg-gray-50 py-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          {/* Header with Animation - Hero Style Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-16 items-center mb-20">
            <div>
              <div className="text-[#FF3621] text-xl font-bold tracking-wide uppercase mb-4">
                SOLUTIONS BY INDUSTRY
              </div>
              <h2 className="text-5xl font-normal mb-4 text-text-dark tracking-[-1px]">
                AI That Delivers Measurable Impact
              </h2>
              <p className="text-lg text-text-gray">
                Real transformations happening across Bangladesh's critical
                sectors
              </p>
            </div>
            <div className="flex justify-center items-center max-w-[400px] mx-auto">
              <LottieAnimation
                src="/animations/solutions-animation-themed.json"
                trigger="scroll"
                loop={true}
                autoplay={false}
                speed={1}
                className="w-80 h-80"
                fallback={
                  <div className="w-80 h-80 bg-bg-light rounded-full flex items-center justify-center">
                    <div className="text-text-gray text-lg">
                      Solutions Animation
                    </div>
                  </div>
                }
              />
            </div>
          </div>

          {/* Featured Solution */}
          <div className="mb-12">
            <Link
              to="/enterprise-ai-platform-deployment"
              className="block bg-white border-2 border-accent-red rounded-lg p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group"
            >
              <div className="text-center mb-6">
                <div className="text-4xl mb-4">🚀</div>
                <h3 className="text-3xl font-bold mb-4 text-text-dark group-hover:text-accent-red transition-colors">
                  Enterprise AI Platform Deployment
                </h3>
                <div className="text-accent-red font-semibold text-lg mb-4">
                  Custom AI Solutions That Transform Your Business
                </div>
                <p className="text-text-gray max-w-3xl mx-auto">
                  Deploy cutting-edge AI platforms that integrate seamlessly
                  with your existing systems and deliver measurable business
                  impact. From Large Language Models to Computer Vision systems,
                  we build custom AI solutions tailored to your organization's
                  needs.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="font-semibold text-text-dark mb-2">
                    🤖 Large Language Models
                  </div>
                  <div className="text-text-gray text-sm">
                    75% faster document processing
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-text-dark mb-2">
                    👁️ Computer Vision
                  </div>
                  <div className="text-text-gray text-sm">
                    90% faster quality inspection
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-text-dark mb-2">
                    📊 Predictive Analytics
                  </div>
                  <div className="text-text-gray text-sm">
                    35% better forecasting accuracy
                  </div>
                </div>
              </div>

              <div className="text-center mt-6">
                <span className="inline-flex items-center text-accent-red font-semibold group-hover:gap-3 transition-all">
                  Learn More About Enterprise AI Deployment
                  <svg
                    className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </span>
              </div>
            </Link>
          </div>

          {/* Industry Solutions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-15">
            {/* Government & Defense */}
            <div className="bg-white border border-border-light rounded-lg p-10 hover:shadow-lg hover:-translate-y-1 hover:border-accent-red transition-all duration-300">
              <div className="text-3xl mb-4">🛡️</div>
              <h3 className="text-2xl font-semibold mb-6 text-text-dark">
                Government & Defense
              </h3>

              <div className="space-y-5">
                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Intelligence Analysis
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    Process thousands of communications daily
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    From 10-20 manual reviews to thousands of Bangla/English
                    intercepts analyzed automatically
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Incident Response
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    5 minutes vs 2 hours
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Generate comprehensive situation reports instantly instead
                    of manual compilation
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Training Simulation
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    Unlimited scenarios vs fixed modules
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Create dynamic training content that adapts to emerging
                    threats
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Services */}
            <div className="bg-white border border-border-light rounded-lg p-10 hover:shadow-lg hover:-translate-y-1 hover:border-accent-red transition-all duration-300">
              <div className="text-3xl mb-4">🏦</div>
              <h3 className="text-2xl font-semibold mb-6 text-text-dark">
                Financial Services
              </h3>

              <div className="space-y-5">
                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Fraud Detection
                  </div>
                  <div className="text-accent-red text-sm font-semibold">
                    <CitationTooltip citation={CITATIONS.mckinsey2025}>
                      30-40% improvement in fraud detection
                    </CitationTooltip>
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Analyze transaction narratives and patterns in real-time
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Customer Service
                  </div>
                  <div className="text-accent-red text-sm font-semibold">
                    <CitationTooltip citation={CITATIONS.mckinsey2025}>
                      14% increase in issue resolution per hour
                    </CitationTooltip>
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    24/7 Bangla language support with human-like understanding
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Regulatory Reporting
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    Hours vs weeks
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Generate compliance reports automatically from raw data
                  </div>
                </div>
              </div>
            </div>

            {/* Healthcare */}
            <div className="bg-white border border-border-light rounded-lg p-10 hover:shadow-lg hover:-translate-y-1 hover:border-accent-red transition-all duration-300">
              <div className="text-3xl mb-4">⚕️</div>
              <h3 className="text-2xl font-semibold mb-6 text-text-dark">
                Healthcare
              </h3>

              <div className="space-y-5">
                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Clinical Documentation
                  </div>
                  <div className="text-accent-red text-sm font-semibold">
                    <CitationTooltip citation={CITATIONS.jama2024}>
                      Save physicians 1.5-2 hours daily
                    </CitationTooltip>
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Convert Bangla voice notes to structured medical records
                    instantly
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Rural Diagnosis Support
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    Specialist insights everywhere
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Enable village clinics to access AI-powered medical
                    expertise
                  </div>
                </div>
              </div>
            </div>

            {/* Manufacturing & RMG */}
            <div className="bg-white border border-border-light rounded-lg p-10 hover:shadow-lg hover:-translate-y-1 hover:border-accent-red transition-all duration-300">
              <div className="text-3xl mb-4">🏭</div>
              <h3 className="text-2xl font-semibold mb-6 text-text-dark">
                Manufacturing & RMG
              </h3>

              <div className="space-y-5">
                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Quality Control
                  </div>
                  <div className="text-accent-red text-sm font-semibold">
                    <CitationTooltip citation={CITATIONS.wef2025}>
                      Up to 25% reduction in quality defects
                    </CitationTooltip>
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Analyze defect reports in natural language to prevent issues
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-text-dark mb-1">
                    Buyer Communication
                  </div>
                  <div className="text-[#FF3621] text-sm font-semibold">
                    Instant multilingual responses
                  </div>
                  <div className="text-text-gray text-sm mt-1">
                    Auto-translate and respond to international buyers with
                    perfect context
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Tecbyte */}
      <section className="py-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <div className="text-[#FF3621] text-xl font-bold tracking-wide uppercase mb-4">
              WHY TECBYTE
            </div>
            <h2 className="text-5xl font-normal mb-12 text-text-dark tracking-[-1px]">
              Built for Bangladesh's AI Future
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* Promise Item 1 */}
              <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-center gap-4">
                  <span className="text-[#FF3621] flex-shrink-0 mt-1">→</span>
                  <p className="text-text-dark leading-relaxed text-left">
                    Access to leading AI models (
                    <span className="md:whitespace-nowrap">
                      <CitationTooltip
                        citation={{
                          source: "Latest AI Models",
                          title: "Industry's most advanced AI systems",
                          year: "2024-2025",
                          description:
                            "Claude 4.0, GPT o3, and Gemini 2.5 Pro represent the cutting edge of AI capabilities",
                        }}
                      >
                        Claude 4.0, GPT o3, Gemini 2.5 Pro
                      </CitationTooltip>
                    </span>
                    )
                  </p>
                </div>
              </div>

              {/* Promise Item 2 */}
              <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex justify-start ">
                <div className="flex items-center gap-4">
                  <span className="text-[#FF3621] flex-shrink-0 mt-1">→</span>
                  <p className="text-text-dark leading-relaxed text-left text-balance">
                    <span>Bangla-first AI development</span>
                  </p>
                </div>
              </div>

              {/* Promise Item 3 */}
              <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <span className="text-[#FF3621] flex-shrink-0 mt-1">→</span>
                  <p className="text-text-dark leading-relaxed text-left text-balance">
                    <span>Enterprise security from day one</span>
                  </p>
                </div>
              </div>

              {/* Promise Item 4 */}
              <div className="bg-white border border-border-light rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <span className="text-[#FF3621] flex-shrink-0 mt-1">→</span>
                  <p className="text-text-dark leading-relaxed text-left text-balance">
                    <span>Your success is our only metric</span>
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xl text-text-dark font-medium">
              We're new. We're focused. We're ready.
            </p>
          </div>
        </div>
      </section>

      {/* Team & Expertise */}
      <section id="team-capabilities" className="py-24 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-20">
            <div className="text-[#FF3621] text-xl font-bold tracking-wide uppercase mb-4">
              EXPERTISE
            </div>
            <h2 className="text-5xl font-normal mb-4 text-text-dark tracking-[-1px]">
              Team & Capabilities
            </h2>
            <p className="text-lg text-text-gray mb-6">
              The right minds to compress your century
            </p>
            <p className="text-lg text-text-gray">
              <span className="font-bold text-text-dark">12 Disciplines.</span>{" "}
              <span className="text-[#FF3621]">One Mission.</span>{" "}
              <span className="font-bold text-text-dark">
                Your Leap Forward.
              </span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* AI & ML Engineers */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                AI & ML Engineers
              </h3>
              <p className="text-text-gray leading-relaxed">
                Turn possibilities into production realities
              </p>
            </div>

            {/* Process Automation Experts */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                Process Automation Experts
              </h3>
              <p className="text-text-gray leading-relaxed">
                Eliminate the repetitive, amplify the creative
              </p>
            </div>

            {/* Data Intelligence Teams */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                Data Intelligence Teams
              </h3>
              <p className="text-text-gray leading-relaxed">
                From chaos to clarity, noise to insight
              </p>
            </div>

            {/* Security Guardians */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                Security Guardians
              </h3>
              <p className="text-text-gray leading-relaxed">
                Fast transformation, bulletproof protection
              </p>
            </div>

            {/* Bangla NLP Pioneers */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2S9 3.34 9 5V11C9 12.66 10.34 14 12 14ZM10.5 5C10.5 4.17 11.17 3.5 12 3.5S13.5 4.17 13.5 5V11C13.5 11.83 12.83 12.5 12 12.5S10.5 11.83 10.5 11V5ZM12 15.5C9.24 15.5 7 13.26 7 10.5H5.5C5.5 14.14 8.36 17 12 17S18.5 14.14 18.5 10.5H17C17 13.26 14.76 15.5 12 15.5Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                Bangla NLP Pioneers
              </h3>
              <p className="text-text-gray leading-relaxed">
                AI that speaks your market's language
              </p>
            </div>

            {/* Integration Architects */}
            <div className="group bg-white border border-border-light rounded-xl p-8 text-center hover:shadow-xl hover:-translate-y-2 hover:border-accent-red transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-accent-red to-red-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17 12C17 14.21 15.21 16 13 16H11V14H13C14.1 14 15 13.1 15 12S14.1 10 13 10H11V8H13C15.21 8 17 9.79 17 12ZM11 16H7V14H11V16ZM7 8H11V10H7V8ZM7 12H11V14H7V12Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4 text-text-dark">
                Integration Architects
              </h3>
              <p className="text-text-gray leading-relaxed">
                Connect yesterday's investments to tomorrow's returns
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link
              to="/about#team-capabilities"
              className="bg-accent-red text-white px-8 py-3 rounded-lg font-medium hover:bg-accent-red-hover transition-colors inline-flex items-center gap-2"
            >
              Explore All 12 Disciplines
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-text-dark text-white py-20 px-6">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-[40px] font-normal mb-8 tracking-[-1px]">
            Questions? Let's talk.
          </h2>
          <Link
            to="/contact"
            className="inline-block bg-accent-red text-white px-8 py-4 rounded-lg font-medium hover:bg-accent-red-hover transition-colors mb-6"
          >
            Schedule a Call
          </Link>
          <div className="text-lg text-gray-200">hello@tecbyte.io</div>
        </div>
      </section>
    </div>
  );
}
